import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { Button, Input, Table, Details, ErrorSummary } from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { getApplicationDocumentId } from '../../features/application/applicationJourneySlice';
import {
  getCostOfInstallation,
  getUploadedFile,
  setCostOfInstallation,
  setUploadedFile,
  setTotalGrantAmount,
  getIndividualChargepointDetails,
} from '../../features/installer/installerJourneySlice';
import readFileAsDataURL from '../../utils/readFileAsDataUrl';
import { checkValidInstallationCost } from '../../regex/regex';
import { submitInvoiceData, deleteInvoice } from '../../firebase';
import useTitle from '../../hooks/useTitle';

const InstallationCost = () => {
  const [errorMsg, setErrorMsg] = useState();
  const [ErrorUploadInvoice, setUploadInvoiceError] = useState();
  const [errorList, setErrorList] = useState([]);

  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'installer.InstallationCost.s27',
    'What is the total cost of installation? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const errorArray = [];

  const costOfInstallationRedux = useSelector(getCostOfInstallation);
  const documentId = useSelector(getApplicationDocumentId);
  const chargepointDetailsRedux = useSelector(getIndividualChargepointDetails);
  const uploadedFileRedux = useSelector(getUploadedFile);
  const validFileTypes = [
    'image/png',
    'image/gif',
    'image/bmp',
    'image/jpeg',
    'image/jpg',
    'application/vnd.ms-excel',
    'application/msword',
    'application/pdf',
  ];
  let uploadedFileValue;

  const blankError = {
    children: copyProvider.getCopy(
      'installer.InstallationCost.s9',
      'Enter a cost of installation'
    ),
  };

  const invalidFormatError = {
    children: copyProvider.getCopy(
      'installer.InstallationCost.s11',
      'Cost of installation must contain numbers and can contain a single "."'
    ),
  };

  const uploadInvoiceError = {
    children: copyProvider.getCopy(
      'installer.InstallationCost.s12',
      'Upload an invoice'
    ),
  };

  const invalidSizeError = {
    children: copyProvider.getCopy(
      'installer.InstallationCost.s13',
      'Attachment cannot exceed 2MB in size'
    ),
  };

  const invalidFileFormatError = {
    children: copyProvider.getCopy(
      'installer.InstallationCost.s14',
      'Attachment must be one of the following file formats (JPG, PNG, GIF, BMP, XLS, DOC or PDF)'
    ),
  };

  const invalidFormatAndSizeError = {
    children: copyProvider.getCopy(
      'installer.InstallationCost.s15',
      'Attachment must be one of the following file formats (JPG, PNG, GIF, BMP, XLS, DOC or PDF) and cannot exceed 2MB in size'
    ),
  };

  const handleInstallationCost = (event) => {
    dispatch(setCostOfInstallation(event.target.value));
  };

  const handleUploadedFile = () => {
    let hasError = false;
    // refresh value after change
    uploadedFileValue = document.getElementById('uploaded-file');
    if (
      !validFileTypes.includes(uploadedFileValue?.files[0]?.type) &&
      uploadedFileValue?.files[0]?.size > 2097152
    ) {
      setUploadInvoiceError(invalidFormatAndSizeError);
      errorArray.push({ ...invalidFormatAndSizeError, href: '#uploaded-file' });
      hasError = true;
    } else if (!validFileTypes.includes(uploadedFileValue?.files[0]?.type)) {
      setUploadInvoiceError(invalidFileFormatError);
      errorArray.push({ ...invalidFileFormatError, href: '#uploaded-file' });
      hasError = true;
    } else if (uploadedFileValue.files[0]?.size > 2097152) {
      setUploadInvoiceError(invalidSizeError);
      errorArray.push({ ...invalidSizeError, href: '#uploaded-file' });
      hasError = true;
    } else if (!uploadedFileValue?.files.length) {
      setUploadInvoiceError(uploadInvoiceError);
      errorArray.push({ ...uploadInvoiceError, href: '#uploaded-file' });
      hasError = true;
    }
    if (!hasError) {
      setUploadInvoiceError('');
      dispatch(setUploadedFile(uploadedFileValue));
    } else {
      setErrorList(errorArray);
    }
  };

  const handleDelete = (event, name) => {
    event.preventDefault();
    document.getElementById('uploaded-file').value = '';
    if (errorMsg !== '') {
      setErrorMsg(null);
    }
    dispatch(setUploadedFile(null));
    deleteInvoice({
      id: documentId,
      fileName: name,
    })
      .then((response) => {
        // eslint-disable-next-line no-console
        console.log(response.result);
        event.preventDefault();
      })
      .catch((error) => {
        // TODO: Show an error
        // eslint-disable-next-line no-console
        console.log(error);
        event.preventDefault();
      });
  };

  const handleBtnClick = async () => {
    // Logic for calculating total grant amount (line 119-157)
    // NB: in order to check if logic works correctly start the journey from '/installer/chargepoints-details

    const costOfInstallationReduxParsed = Number(costOfInstallationRedux);

    const maxClaimAmount = chargepointDetailsRedux.reduce((acc, { socket }) => {
      return acc + socket * 350;
    }, 0);

    const faceDiscount = costOfInstallationReduxParsed * 0.75;
    const grantAmount = Math.min(
      Number.parseFloat(maxClaimAmount).toFixed(2),
      Number.parseFloat(faceDiscount).toFixed(2)
    );

    dispatch(setTotalGrantAmount(grantAmount));

    let hasError = false;
    uploadedFileValue = document.getElementById('uploaded-file');

    // cost of installation field
    if (costOfInstallationRedux === '') {
      setErrorMsg(blankError);
      errorArray.push({ ...blankError, href: '#installation-cost' });
      hasError = true;
    } else if (!checkValidInstallationCost.test(costOfInstallationRedux)) {
      setErrorMsg(invalidFormatError);
      errorArray.push({ ...invalidFormatError, href: '#installation-cost' });
      hasError = true;
    } else {
      setErrorMsg(null);
      setErrorList([]);
    }

    // file upload field. NB: file can not be programmatically set when navigating from check your ansewers page
    if (!uploadedFileValue.files.length && !uploadedFileRedux) {
      setUploadInvoiceError(uploadInvoiceError);
      errorArray.push({ ...uploadInvoiceError, href: '#uploaded-file' });
      hasError = true;
    } else if (ErrorUploadInvoice) {
      errorArray.push({ ...ErrorUploadInvoice, href: '#uploaded-file' });
      hasError = true;
    } else {
      setUploadInvoiceError(null);
      setErrorList([]);
    }

    if (!hasError) {
      const uploadedInvoice = await readFileAsDataURL(
        uploadedFileValue?.files[0]
          ? uploadedFileValue?.files[0]
          : uploadedFileRedux.files[0]
      );
      // BE call
      submitInvoiceData({
        id: documentId,
        costOfInstallation: costOfInstallationRedux.toString(),
        totalGrantAmount: grantAmount.toString(),
        invoice: uploadedInvoice,
      })
        .then((res) => {
          if (!hasError && res.data.success) {
            window.scrollTo(0, 0);
            history.push('/installer/grant-amount');
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err.message);
        });
    } else {
      setErrorList(errorArray);
    }
  };

  const pageModel = (
    <>
      {errorList.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'installer.InstallationCost.s1',
          'What is the total cost of installation?'
        )}
      </h1>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'installer.InstallationCost.s2',
          'Include the chargepoint purchase price with VAT and the installation costs.'
        )}
      </p>
      <Input
        errorMessage={errorMsg || null}
        className="govuk-input govuk-input--width-5"
        value={costOfInstallationRedux}
        onChange={(event) => handleInstallationCost(event)}
        label={{
          children: copyProvider.getCopy(
            'installer.InstallationCost.s3',
            'Total installation cost'
          ),
          className: 'govuk-label--s',
        }}
        hint={{
          children: copyProvider.getCopy(
            'installer.InstallationCost.s4',
            'This is the amount your customer would have to pay before you apply the grant discount.'
          ),
        }}
        id="installation-cost"
        name="installation-cost"
        prefix={{
          children: '£',
        }}
        type="text"
      />

      <Details
        summaryChildren={copyProvider.getCopy(
          'installer.InstallationCost.s17',
          'What items can go towards the total cost of installation'
        )}
      >
        <ul>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s18',
              'Cost of the chargepoint unit'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s19',
              'Electrical components'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s20',
              'Civil engineering works'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s21',
              'Labour costs (for installation)'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s22',
              'Hardware costs'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s23',
              'Any onsite one-off software purchases, but not ongoing software support or services'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s24',
              'VAT incurred by the customer'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'installer.InstallationCost.s25',
              'Site survey works (only to do with installation you completed)'
            )}
          </li>
        </ul>
      </Details>

      <h2 className="govuk-heading-s">
        {copyProvider.getCopy(
          'installer.InstallationCost.s5',
          'Upload a copy of the invoice you gave to your customer'
        )}
      </h2>

      <p className="govuk-body">
        {copyProvider.getCopy(
          'installer.InstallationCost.s6',
          'The invoice you sent must show the total installation cost and the discount from the grant.'
        )}
      </p>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'installer.InstallationCost.s7',
          'You can only upload one file. If you sent your customer more than one invoice, add a final invoice that includes your total charges.'
        )}
      </p>

      <Input
        errorMessage={ErrorUploadInvoice || null}
        id="uploaded-file"
        className="app-no-border"
        hint={{
          children: copyProvider.getCopy(
            'installer.InstallationCost.s26',
            'The document needs to be in JPG, PNG, GIF, BMP, XLS, DOC or PDF formats.'
          ),
        }}
        label={{
          children: (
            <span className="govuk-visually-hidden">
              {copyProvider.getCopy(
                'installer.InstallationCost.s5',
                'Upload a file'
              )}
            </span>
          ),
        }}
        type="file"
        accept=".jpg, .png, .gif, .bmp, .xls, .doc, .pdf, .pptx"
        onChange={handleUploadedFile}
      />

      <div className="govuk-button-group govuk-!-margin-top-7">
        <Button onClick={handleBtnClick}>
          {copyProvider.getCopy(
            'installer.InstallationCost.s10',
            'Save and continue'
          )}
        </Button>
      </div>

      {uploadedFileRedux && (
        <div className="app-table-container">
          <Table
            className="app-container-flex"
            rows={[
              {
                cells: [
                  {
                    children: uploadedFileRedux.files[0]?.name,
                    className: 'app-table-cell-one-additional',
                  },
                  {
                    // Check this
                    children: (
                      <Link
                        to="/"
                        onClick={(event) =>
                          handleDelete(event, uploadedFileRedux.files[0]?.name)
                        }
                      >
                        Delete
                      </Link>
                    ),
                    className: 'app-table-cell-two-additional',
                  },
                ],
              },
            ]}
          />
        </div>
      )}
    </>
  );
  return pageModel;
};
export default InstallationCost;
